import { forwardRef } from 'react';
import { Input, Field, mergeClasses } from '@fluentui/react-components';
import type { InputProps } from '@fluentui/react-components';
import { useTextInputStyles } from './TextInput.styles';

/**
 * Props for GEM TextInput component.
 * Extends all native Fluent UI Input props.
 */
export interface TextInputProps extends InputProps {
  /** Label text displayed above the input */
  label?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Validation message displayed below the input */
  validationMessage?: string;
  /** Validation state controlling message styling */
  validationState?: 'error' | 'warning' | 'success' | 'none';
}

/**
 * Themed TextInput — thin wrapper around Fluent UI Input + Field.
 * Applies GEM defaults: appearance="outline", size="large".
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      required,
      validationMessage,
      validationState,
      appearance = 'outline',
      size = 'large',
      className,
      ...rest
    },
    ref
  ) => {
    const styles = useTextInputStyles();

    return (
      <Field
        className={mergeClasses(styles.root, className)}
        label={label}
        required={required}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <Input ref={ref} appearance={appearance} size={size} {...rest} />
      </Field>
    );
  }
);

TextInput.displayName = 'TextInput';
